import { useEffect, useMemo, useState } from "react";
import { ListChecks, Wand2, Loader2, Plus, X, Check, RefreshCw, Gauge, Timer, Hourglass, Pin } from "lucide-react";

const STYLE = {
  open: "border-slate-500/60 text-slate-200 bg-transparent",
  touched: "border-amber-400/60 text-amber-200 bg-amber-500/10",
  covered: "border-emerald-400/60 text-emerald-200 bg-emerald-500/15",
};

export const CoveragePanel = ({ topics, extracting, checking, hasNotes, onExtract, onCheck, onCycle, onAdd, onRemove }) => {
  const [label, setLabel] = useState("");
  const covered = topics.filter((t) => t.status === "covered").length;
  const submit = (e) => {
    e.preventDefault();
    if (label.trim()) { onAdd(label.trim()); setLabel(""); }
  };
  return (
    <section className="iv-panel" data-testid="coverage-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-white/10">
        <span className="eyebrow flex items-center gap-2">
          <ListChecks size={13} /> Coverage
          {topics.length > 0 && <span className="text-slate-300" data-testid="coverage-count">{covered}/{topics.length}</span>}
          {checking && <Loader2 size={13} className="animate-spin text-sky-400" data-testid="coverage-checking" />}
        </span>
        <div className="flex items-center gap-2">
          {topics.length > 0 && <button onClick={onCheck} data-testid="coverage-check-button" className="iv-icon-btn" title="Re-check against transcript"><RefreshCw size={14} /></button>}
          <button onClick={onExtract} disabled={!hasNotes || extracting} data-testid="coverage-extract-button"
            className="flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 px-3 py-1 text-xs transition-colors">
            {extracting ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} {topics.length ? "Re-read notes" : "Pull topics from notes"}
          </button>
        </div>
      </div>
      {topics.length > 0 && <div className="h-1 bg-white/5"><div className="h-full bg-emerald-400 transition-[width] duration-500" style={{ width: `${(covered / topics.length) * 100}%` }} /></div>}
      <div className="p-3">
        {topics.length === 0 && (
          <p className="px-1 pb-2 text-sm text-slate-300" data-testid="coverage-empty">
            {hasNotes ? "Pull the topics out of your prep notes, or add your own below. They tick off as they come up." : "Add prep notes, then pull topics from them — or add topics yourself below."}
          </p>
        )}
        <ul className="flex flex-wrap gap-2">
          {topics.map((t) => (
            <li key={t.id} data-testid="coverage-topic-item" data-status={t.status} className={`group flex items-center gap-1.5 rounded-full border pl-3 pr-1.5 py-1 text-sm transition-colors ${STYLE[t.status]}`}>
              <button onClick={() => onCycle(t.id)} data-testid="coverage-topic-toggle" className="flex items-center gap-1.5" title="Click to change status">
                {t.status === "covered" && <Check size={13} strokeWidth={3} />}
                {t.status === "touched" && <span className="h-1.5 w-1.5 rounded-full bg-amber-300" />}
                <span className={t.status === "covered" ? "line-through decoration-emerald-400/50" : ""}>{t.label}</span>
              </button>
              <button onClick={() => onRemove(t.id)} data-testid="coverage-topic-remove" className="opacity-50 group-hover:opacity-100 focus:opacity-100 hover:text-white transition-opacity"><X size={12} /></button>
            </li>
          ))}
        </ul>
        <form onSubmit={submit} className="mt-3 flex gap-2">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Add a topic to cover..." data-testid="coverage-add-input" className="iv-field flex-1 rounded-md px-3 py-1.5 text-sm outline-none" />
          <button data-testid="coverage-add-button" className="rounded-md bg-white/10 hover:bg-white/20 px-2.5 transition-colors"><Plus size={14} /></button>
        </form>
        {topics.length > 0 && <p className="mt-2 text-[0.78rem] font-mono text-slate-400">open · <span className="text-amber-300">touched</span> · <span className="text-emerald-300">covered</span> — click a topic to change it</p>}
      </div>
    </section>
  );
};

const words = (t) => t.split(/\s+/).filter(Boolean).length;
const WINDOW_MS = 5 * 60 * 1000;

function split(segments, since = 0) {
  let host = 0, guest = 0;
  for (const s of segments) {
    if (s.ts < since) continue;
    if (s.speaker === "host") host += words(s.text);
    else guest += words(s.text);
  }
  const total = host + guest;
  return { host, guest, total, hostPct: total ? Math.round((host / total) * 100) : 0 };
}

const Bar = ({ data, testId }) => (
  <div className="flex h-2.5 overflow-hidden rounded-full bg-white/10" data-testid={testId}>
    <div className="bg-sky-400 transition-[width] duration-500" style={{ width: `${data.hostPct}%` }} />
    <div className="bg-emerald-400 transition-[width] duration-500" style={{ width: `${data.total ? 100 - data.hostPct : 0}%` }} />
  </div>
);

export const TalkMeter = ({ segments }) => {
  const latest = segments.reduce((m, s) => Math.max(m, s.ts), 0);
  const overall = useMemo(() => split(segments), [segments]);
  const recent = useMemo(() => split(segments, latest - WINDOW_MS), [segments, latest]);
  const tooMuch = recent.total > 60 && recent.hostPct > 40;
  if (!overall.total) return null;
  return (
    <section className="iv-panel px-4 py-3 space-y-2.5" data-testid="talk-meter">
      <div className="flex items-center justify-between gap-2">
        <span className="eyebrow flex items-center gap-2"><Gauge size={14} /> Talk time</span>
        <span className={`text-sm font-medium ${tooMuch ? "text-amber-300" : "text-emerald-300"}`} data-testid="talk-meter-status">{tooMuch ? "You're talking a lot — hand it back" : "Spotlight on your guest"}</span>
      </div>
      <div className="space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-sky-300" data-testid="talk-meter-host-pct">You {overall.hostPct}%</span>
          <span className="text-emerald-300" data-testid="talk-meter-guest-pct">Guest {100 - overall.hostPct}%</span>
        </div>
        <Bar data={overall} testId="talk-meter-overall-bar" />
      </div>
      <div className="flex items-center gap-3">
        <span className="shrink-0 text-xs font-mono uppercase tracking-widest text-slate-400">Last 5 min</span>
        <div className="flex-1"><Bar data={recent} testId="talk-meter-recent-bar" /></div>
        <span className="shrink-0 text-xs font-mono text-slate-300" data-testid="talk-meter-recent-pct">{recent.hostPct}% you</span>
      </div>
    </section>
  );
};

const STATUS = {
  idle: ["text-slate-300", "Set your target, then start"],
  none: ["text-slate-300", "Add prep topics to track pace"],
  ahead: ["text-emerald-300", "Ahead of pace"],
  on: ["text-emerald-300", "On pace"],
  behind: ["text-amber-300", "Behind — move on soon"],
  over: ["text-rose-300", "Over time"],
  done: ["text-emerald-300", "All topics covered"],
};

function pace(elapsedMin, targetMin, topics) {
  const total = topics.length;
  const covered = topics.filter((t) => t.status === "covered").length;
  const touched = topics.filter((t) => t.status === "touched").length;
  const progress = total ? (covered + touched * 0.5) / total : 0;
  const timeFrac = Math.min(1, elapsedMin / targetMin);
  const left = targetMin - elapsedMin;
  const open = total - covered;
  let status = "on";
  if (!total) status = "none";
  else if (open === 0) status = "done";
  else if (elapsedMin < 0.5) status = "idle";
  else if (left <= 0) status = "over";
  else if (progress < timeFrac - 0.12) status = "behind";
  else if (progress > timeFrac + 0.12) status = "ahead";
  return { total, covered, progress, timeFrac, left, open, status, perTopic: open && left > 0 ? left / open : 0 };
}

const Row = ({ label, frac, color, value, testId }) => (
  <div className="flex items-center gap-3">
    <span className="w-14 shrink-0 text-xs font-mono uppercase tracking-widest text-slate-400">{label}</span>
    <div className="flex-1 h-2.5 rounded-full bg-white/10 overflow-hidden"><div className={`h-full ${color} transition-[width] duration-700`} style={{ width: `${Math.round(frac * 100)}%` }} data-testid={testId} /></div>
    <span className="w-16 shrink-0 text-right text-sm text-slate-200">{value}</span>
  </div>
);

export const PacingPanel = ({ elapsedMs, targetMin, setTargetMin, topics }) => {
  const [draft, setDraft] = useState(String(targetMin));
  useEffect(() => setDraft(String(targetMin)), [targetMin]);
  const commit = () => {
    const n = Math.min(300, Math.max(1, Math.round(Number(draft)) || targetMin));
    setTargetMin(n);
    setDraft(String(n));
  };
  const elapsedMin = elapsedMs / 60000;
  const p = pace(elapsedMin, targetMin, topics);
  const [color, text] = STATUS[p.status];
  return (
    <section className="iv-panel px-4 py-3 space-y-2.5" data-testid="pacing-panel" data-status={p.status}>
      <div className="flex items-center justify-between gap-2">
        <span className="eyebrow flex items-center gap-2"><Timer size={14} /> Pacing</span>
        <label className="flex items-center gap-1.5 text-sm text-slate-300">
          Target
          <input type="number" min={1} max={300} step={5} value={draft} data-testid="pacing-target-input" onChange={(e) => setDraft(e.target.value)} onBlur={commit}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="iv-field w-16 rounded-md px-2 py-0.5 text-sm text-center outline-none" />
          min
        </label>
      </div>
      <p className={`text-base font-medium ${color}`} data-testid="pacing-status">{text}</p>
      <Row label="Time" frac={p.timeFrac} color={p.status === "over" ? "bg-rose-400" : "bg-sky-400"} value={`${Math.floor(elapsedMin)}/${targetMin}m`} testId="pacing-time-bar" />
      <Row label="Topics" frac={p.progress} color="bg-emerald-400" value={p.total ? `${p.covered}/${p.total}` : "—"} testId="pacing-topic-bar" />
      {p.total > 0 && p.open > 0 && (
        <p className="text-sm text-slate-300" data-testid="pacing-hint">
          {p.left > 0 ? `${Math.ceil(p.left)} min left · about ${p.perTopic.toFixed(1)} min per open topic` : `${Math.ceil(-p.left)} min over · ${p.open} topic${p.open > 1 ? "s" : ""} still open`}
        </p>
      )}
    </section>
  );
};

export const WrapUpNudge = ({ minutesLeft, openTopics, questions, loading, onRefresh, onDismiss, isPinned, onPin }) => (
  <section className="iv-panel border-amber-400/50 bg-amber-950/40" data-testid="wrapup-nudge">
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 px-4 py-3 border-b border-amber-400/20">
      <span className="eyebrow flex items-center gap-2 whitespace-nowrap text-amber-200">
        <Hourglass size={14} className="text-amber-300" /> Time to wrap up
        {loading && <Loader2 size={14} className="animate-spin text-amber-300" data-testid="wrapup-loading" />}
      </span>
      <div className="flex items-center gap-1">
        <span className="whitespace-nowrap text-base font-semibold text-amber-200 mr-2" data-testid="wrapup-minutes-left">{minutesLeft > 0 ? `${Math.ceil(minutesLeft)} min left` : `${Math.ceil(-minutesLeft)} min over`}</span>
        <button onClick={onRefresh} disabled={loading} title="New closing questions" data-testid="wrapup-refresh-button" className="iv-icon-btn"><RefreshCw size={15} /></button>
        <button onClick={onDismiss} title="Dismiss" data-testid="wrapup-dismiss-button" className="iv-icon-btn"><X size={15} /></button>
      </div>
    </div>
    <div className="px-4 py-3 space-y-3">
      {openTopics.length > 0 && <p className="text-sm text-slate-200" data-testid="wrapup-open-topics"><span className="font-mono text-xs uppercase tracking-widest text-amber-300">Still open · </span>{openTopics.join(" · ")}</p>}
      {questions.length === 0 && !loading && <p className="text-sm text-slate-300">Closing questions will appear here.</p>}
      <ul className="space-y-2" data-testid="wrapup-questions">
        {questions.map((q, i) => (
          <li key={`${q.text}-${i}`} className="flex items-start gap-2" data-testid="wrapup-question-item">
            <p className="flex-1 text-lg leading-snug text-white">{q.text}</p>
            <button onClick={() => onPin(q)} title="Pin" data-testid="wrapup-pin-button" className={`iv-icon-btn ${isPinned(q.text) ? "text-amber-400" : ""}`}><Pin size={15} fill={isPinned(q.text) ? "currentColor" : "none"} /></button>
          </li>
        ))}
      </ul>
    </div>
  </section>
);
